import { useState } from "react";
import { PencilLine } from "lucide-react";
import { getP } from "../../lib/casePath";

export const Card = ({ title, subtitle, children, className = "", testid, right }) => (
  <section className={`min-w-0 rounded-2xl border border-[#E2DDD5] bg-white p-4 shadow-sm transition-shadow hover:shadow-md sm:p-8 ${className}`} data-testid={testid}>
    {(title || right) && (
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          {title && <h3 className="font-serif-display text-xl font-bold">{title}</h3>}
          {subtitle && <p className="mt-1 text-sm text-[#5C5850]">{subtitle}</p>}
        </div>
        {right}
      </div>
    )}
    {children}
  </section>
);

const base = "w-full rounded-xl border border-[#E2DDD5] bg-[#FAF8F5] px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-[#E8643C] focus:bg-white";
const tid = (k) => `field-${k.replace(/\./g, "-")}`;

const MoneyInput = ({ value, onChange, k }) => {
  const [focus, setFocus] = useState(false);
  const shown = focus ? (value ?? "") : (value || value === 0 ? Number(value).toLocaleString("es-MX", { maximumFractionDigits: 2 }) : "");
  return (
    <input data-testid={tid(k)} className={`${base} text-right tabular-nums`} inputMode="decimal" value={shown}
      onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
      onChange={(e) => { const s = e.target.value.replace(/[^0-9.-]/g, ""); onChange(s === "" ? 0 : Number(s)); }} />
  );
};

export const Input = ({ f, value, onChange }) => {
  if (f.type === "money" || f.type === "number") {
    if (f.type === "money") return <MoneyInput k={f.k} value={value} onChange={onChange} />;
    return <input data-testid={tid(f.k)} type="number" step={f.step || "any"} className={`${base} text-right tabular-nums`} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))} />;
  }
  if (f.type === "select") {
    return (
      <select data-testid={tid(f.k)} className={base} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">Selecciona…</option>
        {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    );
  }
  if (f.type === "bool") {
    return (
      <button type="button" data-testid={tid(f.k)} onClick={() => onChange(!value)}
        className={`flex h-[42px] w-full items-center justify-between rounded-xl border px-3.5 text-sm transition-colors ${value ? "border-[#E8643C] bg-[#FDF0EB] text-[#111]" : "border-[#E2DDD5] bg-[#FAF8F5] text-[#5C5850]"}`}>
        {value ? "Sí" : "No"}
        <span className={`relative h-5 w-9 rounded-full transition-colors ${value ? "bg-[#E8643C]" : "bg-[#E2DDD5]"}`}><span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${value ? "translate-x-4" : "translate-x-0.5"}`} /></span>
      </button>
    );
  }
  return <input data-testid={tid(f.k)} type={f.type === "date" ? "date" : "text"} className={base} value={value ?? ""}
    onChange={(e) => onChange(f.upper ? e.target.value.toUpperCase() : e.target.value)} placeholder={f.ph} />;
};

export const FieldGrid = ({ fields, data, onChange, edited = [], cols = 3 }) => (
  <div className={`grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2 ${cols === 3 ? "lg:grid-cols-3" : ""}`}>
    {fields.filter((f) => !f.show || f.show(data)).map((f) => {
      const v = getP(data, f.k);
      const err = f.validate ? f.validate(v, data) : null;
      return (
        <label key={f.k} className={f.wide ? "sm:col-span-2 lg:col-span-3" : ""}>
          <span className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-[#8C867A]">
            {f.l}{f.req && <span className="text-[#E8643C]">*</span>}
            {edited.includes(f.k) && <PencilLine size={12} className="text-amber-600" />}
          </span>
          <Input f={f} value={v} onChange={(nv) => onChange(f.k, nv)} />
          {err && <span className="mt-1 block text-xs text-rose-600" data-testid={`${tid(f.k)}-error`}>{err}</span>}
          {f.help && !err && <span className="mt-1 block text-xs text-[#8C867A]">{f.help}</span>}
        </label>
      );
    })}
  </div>
);

export const Btn = ({ kind = "primary", className = "", ...p }) => {
  const k = {
    primary: "bg-[#111111] text-white hover:bg-[#1F1F1F] shadow-sm",
    accent: "bg-[#E8643C] text-white hover:bg-[#D5522B] shadow-md shadow-[#E8643C]/20",
    secondary: "bg-[#F2F0EC] text-[#111] hover:bg-[#E2DDD5] border border-[#E2DDD5]",
    ghost: "text-[#5C5850] hover:bg-[#F2F0EC]",
  }[kind];
  return <button {...p} className={`inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold transition-all active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50 ${k} ${className}`} />;
};

export const Pill = ({ cls, children, testid }) => <span data-testid={testid} className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${cls}`}>{children}</span>;
